import { type CacheConfig, type CachedResponse, type FetchPolicy } from './types.ts';

const DEFAULT_MAX_SIZE = 100;
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Recursively canonicalises a value for deterministic `JSON.stringify`
 * output. Object keys are sorted at every nesting level, objects inside
 * arrays are canonicalised too, and `undefined` is normalised to `null`
 * so it survives serialisation. Primitives and array order are preserved.
 *
 * Without this, using `JSON.stringify(variables, topLevelKeys.sort())`
 * only sorts top-level keys — the array replacer acts as a whitelist at
 * every nesting level, stripping nested keys (e.g. `projectId` inside
 * `project: { projectId }`) and producing identical cache keys for
 * different variable values.
 */
const canonicalizeValue = (v: unknown): unknown => {
  if (v === null || v === undefined) {
    return null;
  }
  if (Array.isArray(v)) {
    return v.map(canonicalizeValue);
  }
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const k of Object.keys(obj).sort()) {
      sorted[k] = canonicalizeValue(obj[k]);
    }
    return sorted;
  }
  return v;
};

/**
 * In-memory cache for GraphQL responses.
 * Supports TTL-based expiration and LRU eviction.
 */
export class GraphQLCache {
  private cache: Map<string, CachedResponse>;
  private accessLog: Map<string, number>;
  private accessCounter: number;
  private readonly maxSize: number;
  private readonly defaultTTL: number;
  private readonly enabled: boolean;

  constructor(config: CacheConfig = {}) {
    this.cache = new Map();
    this.accessLog = new Map();
    this.accessCounter = 0;
    this.maxSize = config.maxSize ?? DEFAULT_MAX_SIZE;
    this.defaultTTL = config.defaultTTL ?? DEFAULT_TTL;
    this.enabled = config.enabled ?? true;
  }

  /**
   * Generate a cache key from query string and variables.
   * Canonicalizes variables (sorted keys at all levels) so different variable
   * shapes produce different keys and the same logical variables produce the same key.
   */
  generateKey(query: string, variables?: object): string {
    const variablesKey = variables ? JSON.stringify(canonicalizeValue(variables)) : '';
    return `${query}:${variablesKey}`;
  }

  /**
   * Get a cached response by key.
   * Returns null if not found or expired.
   */
  get<T>(key: string): CachedResponse<T> | null {
    if (!this.enabled) {
      return null;
    }

    const cached = this.cache.get(key) as CachedResponse<T> | undefined;
    if (!cached) {
      return null;
    }

    if (this.isExpired(cached)) {
      this.evict(key);
      return null;
    }

    this.accessLog.set(key, ++this.accessCounter);
    return cached;
  }

  /**
   * Store a response in the cache.
   */
  set<T>(key: string, data: T, ttl?: number): void {
    if (!this.enabled) {
      return;
    }

    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }

    const cached: CachedResponse<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl ?? this.defaultTTL,
      key,
    };

    this.cache.set(key, cached);
    this.accessLog.set(key, ++this.accessCounter);
  }

  /**
   * Evict a specific key from the cache.
   */
  evict(key: string): void {
    this.cache.delete(key);
    this.accessLog.delete(key);
  }

  /**
   * Clear all entries from the cache.
   */
  clear(): void {
    this.cache.clear();
    this.accessLog.clear();
  }

  /**
   * Remove all expired entries from the cache.
   */
  prune(): void {
    for (const [key, cached] of this.cache.entries()) {
      if (this.isExpired(cached)) {
        this.evict(key);
      }
    }
  }

  /**
   * Get the current size of the cache.
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Determine if cache should be used based on fetch policy.
   */
  shouldReadFromCache(fetchPolicy: FetchPolicy = 'cache-first'): boolean {
    return (
      fetchPolicy === 'cache-first' ||
      fetchPolicy === 'cache-only' ||
      fetchPolicy === 'cache-and-network'
    );
  }

  /**
   * Determine if response should be written to cache based on fetch policy.
   */
  shouldWriteToCache(fetchPolicy: FetchPolicy = 'cache-first'): boolean {
    return fetchPolicy !== 'no-cache';
  }

  private isExpired(cached: CachedResponse): boolean {
    return Date.now() - cached.timestamp > cached.ttl;
  }

  private evictLRU(): void {
    let oldestKey: string | undefined;
    let oldestTime = Infinity;

    for (const [key, time] of this.accessLog.entries()) {
      if (time < oldestTime) {
        oldestTime = time;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.evict(oldestKey);
    }
  }
}
